import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import ReactMarkdown from 'react-markdown';
import SparkIcon from './SparkIcon';

const useTypewriter = (content, isStreaming) => {
  const [displayedLength, setDisplayedLength] = useState(0);
  const prevContentRef = useRef('');

  useEffect(() => {
    if (!isStreaming) {
      setDisplayedLength(content.length);
      prevContentRef.current = content;
      return;
    }

    if (content.length <= displayedLength) {
      return;
    }

    const charsToReveal = content.length - displayedLength;
    const speed = charsToReveal > 10 ? 5 : 15;

    const timer = setTimeout(() => {
      setDisplayedLength((prev) => Math.min(prev + 3, content.length));
    }, speed);

    return () => clearTimeout(timer);
  }, [content, displayedLength, isStreaming]);

  useEffect(() => {
    if (!isStreaming && content.length > 0) {
      setDisplayedLength(content.length);
    }
  }, [isStreaming, content.length]);

  return content.slice(0, displayedLength);
};

const MessageRenderer = ({ message, isPage }) => {
  const { role, content, error, isStreaming } = message;
  const displayedContent = useTypewriter(
    content,
    role === 'assistant' && isStreaming
  );

  const getMessageClass = () => {
    const baseClass = 'ai-assistant-sidebar__message';
    const roleClass = `${baseClass}--${role}`;
    const errorClass = error ? `${baseClass}--error` : '';

    return `${baseClass} ${roleClass} ${errorClass}`.trim();
  };

  const renderContent = role === 'assistant' ? displayedContent : content;

  // When rendered inside the page, only output the markdown content
  if (isPage) {
    return (
      <>
        <ReactMarkdown>{renderContent}</ReactMarkdown>
        {isStreaming && (
          <span className="ai-assistant-sidebar__cursor" />
        )}
      </>
    );
  }

  return (
    <div className={getMessageClass()}>
      {role === 'assistant' && (
        <div className="ai-assistant-sidebar__message-header">
          <span className="ai-assistant-sidebar__message-header-icon">
            <SparkIcon size={13} />
          </span>
          <span className="ai-assistant-sidebar__message-header-name">AI Assistant</span>
        </div>
      )}
      <div className="ai-assistant-sidebar__message-content">
        {role === 'user' ? (
          <p>{renderContent}</p>
        ) : (
          <>
            <ReactMarkdown>{renderContent}</ReactMarkdown>
            {isStreaming && (
              <span className="ai-assistant-sidebar__cursor" />
            )}
          </>
        )}
      </div>
      <div className="ai-assistant-sidebar__message-timestamp">
        {new Date(message.timestamp).toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit'
        })}
      </div>
    </div>
  );
};

MessageRenderer.propTypes = {
  message: PropTypes.shape({
    id: PropTypes.string.isRequired,
    role: PropTypes.string.isRequired,
    content: PropTypes.string.isRequired,
    timestamp: PropTypes.string.isRequired,
    error: PropTypes.bool,
    isStreaming: PropTypes.bool
  }).isRequired,
  isPage: PropTypes.bool
};

export default MessageRenderer;
